/**
 *
 *  TimeSlotsPicker
 *  TimeSlotsPicker Component
 *
 *  Created by Vladimir Shishlyannikov on 12/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import PropTypes from "prop-types";

import moment from "moment-timezone";

// Components
import Calendar from "./Calendar/Calendar";
import TimeList from "./TimeList/TimeList";

// Utils
import * as dates from "../../utils/dates";
import { isDateAvailable, getMomentWithTz, merge } from "../../utils/date";
import { uniqueId } from "../../utils/_";
import CalendarCellView from "./CalendarCell/CalendarCell";

var propTypes = {
  value: PropTypes.instanceOf(Date),

  /**
   * @example ['onChangePicker', [ ['new Date()', null] ]]
   */
  onChange: PropTypes.func,

  /**
   * Default current date at which the calendar opens. If none is provided, opens at today's date or the `value` date (if any).
   */
  currentDate: PropTypes.instanceOf(Date),

  /**
   * Change event Handler that is called when the currentDate is changed. The handler is called with the currentDate object.
   */
  onCurrentDateChange: PropTypes.func,
  onSelect: PropTypes.func,

  /**
   * Available days of the week. If not present then it will be disabled.
   *
   * @example ['prop', ['availableDays', '['2018-11-03', ..]']]
   */
  availableDays: PropTypes.array,

  /**
   * Multiple time intervals for particular day. Should be sorted beforehand.
   *
   * @example ['prop', ['timeSlots', '[{ startTime: new Date(), endTime: new Date() }, ..]']]
   */
  timeSlots: PropTypes.array,

  /**
   * The minimum Date that can be selected. Min only limits selection, it doesn't constrain the date values that
   * can be typed or pasted into the widget. If you need this behavior you can constrain values via
   * the `onChange` handler.
   *
   * @example ['prop', ['min', 'new Date()']]
   */
  min: PropTypes.instanceOf(Date),

  /**
   * The maximum Date that can be selected. Max only limits selection, it doesn't constrain the date values that
   * can be typed or pasted into the widget. If you need this behavior you can constrain values via
   * the `onChange` handler.
   *
   * @example ['prop', ['max', 'new Date()']]
   */
  max: PropTypes.instanceOf(Date),

  /**
   * The amount of minutes between each entry in the time list.
   *
   * @example ['prop', { step: 90 }]
   */
  step: PropTypes.number,

  culture: PropTypes.string,

  /**
   * A formatter used to display the date value. For more information about formats
   * visit the [Localization page](/i18n)
   *
   * @example ['dateFormat', ['format', "{ raw: 'MMM dd, yyyy' }", null, { defaultValue: 'new Date()', time: 'false' }]]
   */
  // format: CustomPropTypes.dateFormat,

  /**
   * A formatter used by the time dropdown to render times. For more information about formats visit
   * the [Localization page](/i18n).
   *
   * @example ['dateFormat', ['timeFormat', "{ time: 'medium' }", null, { date: 'false', open: '"time"' }]]
   */
  timeFormat: PropTypes.string,

  /**
   * A formatter to be used while the date input has focus. Useful for showing a simpler format for inputing.
   * For more information about formats visit the [Localization page](/i18n)
   *
   * @example ['dateFormat', ['editFormat', "{ date: 'short' }", null, { defaultValue: 'new Date()', format: "{ raw: 'MMM dd, yyyy' }", time: 'false' }]]
   */
  // editFormat: CustomPropTypes.dateFormat,

  /**
   * Enable the calendar component of the picker.
   */
  dateEnabled: PropTypes.bool,

  /**
   * Enable the time list component of the picker.
   */
  timeEnabled: PropTypes.bool,

  /**
   * A customize the rendering of times but providing a custom component.
   */
  timeComponent: PropTypes.func,

  namePicker: PropTypes.string,
  autoFocus: PropTypes.bool,
  // disabled: CustomPropTypes.disabled,
  // readOnly: CustomPropTypes.disabled,

  /** @ignore */
  tabIndex: PropTypes.any,
  /** @ignore */
  // "aria-labelledby": PropTypes.string,
  /** @ignore */
  "aria-describedby": PropTypes.string,

  // onBlur: PropTypes.func,
  // onFocus: PropTypes.func,
};

/**
 * ---
 * subtitle: DayPicker, TimeSlotPicker
 * ---
 *
 * @public
 * @extends Calendar
 */
class TimeSlotsPicker extends React.Component {
  static propTypes = propTypes;

  static defaultProps = {
    value: null,
    min: new Date(1900, 0, 1),
    max: new Date(2099, 11, 31),
    dateEnabled: true,
    timeEnabled: true,
  };

  constructor(props) {
    super(props);

    var { value, format, culture, availableDays } = this.props;

    // TODO fix this
    var noon = dates.add(dates.startOf(value, "day"), 810, "minutes"); // 13:30
    var evening = dates.add(dates.startOf(value, "day"), 1110, "minutes"); // 18:30

    this.calRef = React.createRef();
    this.timeRef = React.createRef();

    this.inputId = uniqueId("_input");
    this.dateId = uniqueId("_date");
    this.listId = uniqueId("_listbox");
    this.activeCalendarId = uniqueId("_calendar_active_cell");
    this.activeOptionId = uniqueId("_listbox_active_option");

    this.handleDateSelect = this.handleDateSelect.bind(this);
    this.handleTimeSelect = this.handleTimeSelect.bind(this);

    this.state = {
      focused: false,
      textValue:
        !availableDays ||
        !availableDays.length ||
        isDateAvailable(value, availableDays, culture)
          ? formatDate(value, format, props.region, culture)
          : "",
      dayPart: dates.lte(value, noon)
        ? "morning"
        : dates.gt(value, noon) && dates.lt(value, evening)
        ? "afternoon"
        : "evening",
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    var { value, format, culture, region, availableDays, onChange } = nextProps;

    // TODO fix this
    var noon = dates.add(dates.startOf(value, "day"), 810, "minutes"); // 13:30
    var evening = dates.add(dates.startOf(value, "day"), 1110, "minutes"); // 18:30

    if (onChange) {
      this.setState({
        textValue:
          !availableDays ||
          !availableDays.length ||
          isDateAvailable(value, availableDays, culture)
            ? formatDate(value, format, region, culture)
            : "",
        dayPart: dates.lte(value, noon)
          ? "morning"
          : dates.gt(value, noon) && dates.lt(value, evening)
          ? "afternoon"
          : "evening",
      });
    }
  }

  handleChange(date, str, constrain, isTimeChanged) {
    var { onChange, value, format, culture, region, availableDays } =
      this.props;

    // TODO fix this
    var noon = dates.add(dates.startOf(date, "day"), 810, "minutes"); // 13:30
    var evening = dates.add(dates.startOf(date, "day"), 1110, "minutes"); // 18:30

    if (constrain) {
      date = this.inRangeOrAvailableValue(date);
      // date = this.inRangeTime(date);
    }

    if (onChange) {
      if (date == null || value == null) {
        //eslint-disable-next-line eqeqeq
        if (date != value) {
          onChange(date, str);
        }
      } else if (!dates.eq(date, value)) {
        onChange(date, str);
      }
    }

    if (isTimeChanged) {
      this.setState({
        textValue:
          !availableDays ||
          !availableDays.length ||
          isDateAvailable(date, availableDays, culture)
            ? formatDate(date, format, region, culture)
            : "",
        dayPart: dates.lte(date, noon)
          ? "morning"
          : dates.gt(date, noon) && dates.lt(date, evening)
          ? "afternoon"
          : "evening",
      });
    }
  }

  handleDateSelect(date) {
    const { disabled, region, onSelect } = this.props;
    var format = getFormat(this.props),
      dateTime = merge(date, this.props.value, this.props.currentDate, region),
      dateStr = formatDate(dateTime, format, region, this.props.culture);

    if (!disabled) {
      onSelect && onSelect(dateTime, dateStr);

      this.handleChange(dateTime, dateStr, true);
    }
  }

  handleTimeSelect(datum) {
    const { disabled, region, onSelect } = this.props;
    var format = getFormat(this.props),
      dateTime = this.props.value
        ? merge(this.props.value, datum.date, this.props.currentDate, region)
        : datum.date,
      dateStr = getMomentWithTz(dateTime, region).format(format); // formatDate(dateTime, format, this.props.culture);

    if (!disabled) {
      onSelect && onSelect(dateTime, dateStr, true);
      this.handleChange(dateTime, dateStr, true, true);
    }
  }

  renderCalendar() {
    var { activeCalendarId, dateId } = this;
    var {
      view,
      headerFormat,
      region,
      value,
      min,
      max,
      onCurrentDateChange,
      currentDate,
      availableDays,
    } = this.props;

    return (
      <Calendar
        id={dateId}
        activeId={activeCalendarId}
        tabIndex="-1"
        view={view}
        headerFormat={headerFormat}
        region={region}
        availableDays={availableDays}
        value={value}
        autoFocus={false}
        min={min}
        max={max}
        onChange={this.handleDateSelect}
        // #75: need to aggressively reclaim focus from the calendar otherwise
        // disabled header/footer buttons will drop focus completely from the widget
        // onNavigate={() => this.focus()}
        onNavigate={() => {}}
        currentDate={currentDate}
        onCurrentDateChange={onCurrentDateChange}
        aria-live="polite"
        // aria-labelledby={}
        ref={this.calRef}
        onClear={this.clear}
      />
    );
  }

  renderTimeList() {
    var { activeOptionId, listId } = this;
    var {
      value,
      timeSlots,
      min,
      max,
      step,
      currentDate,
      culture,
      region,
      preserveDate,
      timeFormat,
      timeComponent,
      disabledTimeSlots,
    } = this.props;

    return (
      <TimeList
        id={listId}
        timeSlots={timeSlots}
        disabled={disabledTimeSlots}
        min={min}
        max={max}
        step={step}
        currentDate={currentDate}
        activeId={activeOptionId}
        format={timeFormat}
        culture={culture}
        region={region}
        value={dateOrNull(value)}
        onMove={this.handleScroll}
        onSelect={this.handleTimeSelect}
        preserveDate={preserveDate}
        itemComponent={timeComponent}
        // aria-labelledby={}
        ref={this.timeRef}
      />
    );
  }

  render() {
    var { id, className, date, time } = this.props;

    return (
      <div
        id={id}
        tabIndex={-1}
        className={`${
          className ? className + " " : ""
        }tb-datetime-picker tbk-outline-none`}
      >
        {this.renderCalendar()}
        {this.renderTimeList()}
      </div>
    );
  }

  inRangeOrAvailableValue(value) {
    const { availableDays, culture } = this.props;

    if (value == null) {
      return value;
    }

    if (
      availableDays &&
      availableDays.length &&
      !isDateAvailable(value, availableDays, culture)
    ) {
      return null;
    }

    return dates.max(dates.min(value, this.props.max), this.props.min);
  }
}

export default TimeSlotsPicker;

function getFormat(props) {
  var isDate = props.dateEnabled != null ? props.dateEnabled : true;
  var isTime = props.timeEnabled != null ? props.timeEnabled : true;

  return props.format
    ? props.format
    : (isDate && isTime) || (!isDate && !isTime)
    ? "HH:mm" // TODO: make constants
    : isDate
    ? "YYYY-MM-DD" /*ISO_FORMAT*/ // TODO: make constants
    : "HH:mm"; // TODO: make constants
}

function formatDate(date, format, region, culture) {
  var val = "";

  if (date instanceof Date && !isNaN(date.getTime())) {
    val = getMomentWithTz(date, region).format(format);
  }

  return val;
}

function dateOrNull(dt) {
  if (dt && !isNaN(dt.getTime())) {
    return dt;
  }
  return null;
}
